import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Box, Stack, Typography } from '@mui/material'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { FC, useCallback, useMemo, useState } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { WorkspaceInvitationState, WorkspaceSeatType, WorkspaceUserProfile } from 'models/user'

import { MUIButton } from 'components/common/MUIButton'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import Modal from 'components/common/Modals/Modal/Modal'
import classnames from 'classnames'
import { logger } from 'utils/logging'
import popupStyles from './TransferPropertiesPopup.module.sass'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTransferProperties } from 'dataQueries'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspaceList } from 'utils/hooks/useWorkspaceList'

interface Props {
  isOpen: boolean
  realtyIds: string[]
  onClose: () => void
  onSuccess: () => void
}

export const TransferPropertiesPopup: FC<Props> = ({
  isOpen,
  realtyIds,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation(['properties_dashboard', 'actions'])
  const { userPropertyWorkspaceId } = useUserData()
  const { activeWorkspaceList } = useWorkspaceList()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const transferMutation = useTransferProperties()

  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceUserProfile | undefined>()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isConsentGiven, setIsConsentGiven] = useState(false)

  // Backend requires at least RESTRICTED access in the target workspace; SPONSORED seats are rejected with 403
  const transferableWorkspaces = useMemo(() =>
    activeWorkspaceList.filter(ws =>
      ws.id !== userPropertyWorkspaceId
      && ws.invitationState !== WorkspaceInvitationState.PENDING
      && [WorkspaceSeatType.RESTRICTED, WorkspaceSeatType.FULL].includes(ws.seatType)
    )
    , [activeWorkspaceList, userPropertyWorkspaceId])

  const count = realtyIds.length

  const resetState = useCallback(() => {
    setSelectedWorkspace(undefined)
    setIsDropdownOpen(false)
    setIsConsentGiven(false)
  }, [])

  const handleClose = useCallback(() => {
    onClose()
    transferMutation.reset()
    resetState()
  }, [onClose, transferMutation, resetState])

  const handleConfirm = useCallback(() => {
    if (!selectedWorkspace || !isConsentGiven) return

    transferMutation.mutate({
      workspaceId: userPropertyWorkspaceId,
      realtyIds,
      targetWorkspaceId: selectedWorkspace.id,
      dataTransferConsentGiven: isConsentGiven,
    }, {
      onSuccess: () => {
        logAnalyticsEvent(AnalyticsEvent.TRANSFER_PROPERTIES, { count, targetWorkspaceId: selectedWorkspace.id })
        spawnSuccessToast(t('bulk_actions.transfer_success', { count }))
        onSuccess()
        transferMutation.reset()
        resetState()
      },
      onError: (error) => {
        logger.error('Transfer properties failed', error as Error, {
          context: { component: 'TransferPropertiesPopup', action: 'transfer', metadata: { count, targetWorkspaceId: selectedWorkspace.id } }
        })
        spawnErrorToast(t('bulk_actions.transfer_error'))
      }
    })
  }, [transferMutation, userPropertyWorkspaceId, realtyIds, selectedWorkspace, isConsentGiven, spawnSuccessToast, spawnErrorToast, t, count, onSuccess, resetState])

  const isPending = transferMutation.isPending
  const isConfirmDisabled = !selectedWorkspace || !isConsentGiven || isPending

  return (
    <Modal
      isOpen={isOpen}
      modalContentClassName={popupStyles.modal}
      onClickOutside={handleClose}
      onClose={handleClose}
      title={t('bulk_actions.transfer_title')}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap={1}>

          <MUIButton
            type="secondaryBorder"
            disabled={isPending}
            onClick={handleClose}
          >
            {t('actions:Cancel')}
          </MUIButton>

          <MUIButton
            disabled={isConfirmDisabled}
            isLoading={isPending}
            onClick={handleConfirm}
          >
            {t('bulk_actions.transfer')}
          </MUIButton>

        </Stack>
      }
    >
      <div className={classnames(popupStyles.inside)}>

        <Typography variant="text-md" color={GRAY_700} marginBottom="1rem">
          {t('bulk_actions.transfer_description', { count })}
        </Typography>

        <Box marginBottom="1.2rem">
          <MUIDropdown
            open={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            placement="bottom-start"
            disablePortal={false}
            sx={{ zIndex: 101, '& .MuiPaper-root': { maxHeight: '20rem' } }}
            button={() => (
              <MUIDropdownInput
                type={DropdownInputTypes.SELECTOR}
                label={t('bulk_actions.transfer_workspace_label')}
                value={selectedWorkspace?.name ?? ''}
                placeholder={t('bulk_actions.transfer_workspace_placeholder')}
                readOnly
                disabled={isPending}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              />
            )}
          >
            {transferableWorkspaces.map((workspace) => (
              <MUIDropdownItem
                key={workspace.id}
                isSelected={selectedWorkspace?.id === workspace.id}
                onClick={() => {
                  setSelectedWorkspace(workspace)
                  setIsDropdownOpen(false)
                }}
              >
                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                  {workspace.name}
                </Typography>
              </MUIDropdownItem>
            ))}
          </MUIDropdown>
        </Box>

        <MUICheckbox
          checked={isConsentGiven}
          disabled={isPending}
          onChange={(_, checked) => setIsConsentGiven(checked)}
          label={
            <Typography variant="text-sm" color={GRAY_700}>
              {t('bulk_actions.transfer_consent_label')}
            </Typography>
          }
        />

      </div>
    </Modal>
  )
}
